import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, InputNumber, Radio, Select, Table } from 'antd';
import { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import { getEnumOptionsWithTranslations } from '../../../utils/getEnumOptionsWithTranslations';
import { prePackagedProductUnitEnum } from '../../../enums/MeasuringUnitEnum';
import DefaultButton from '../../../components/buttons/DefaultButton';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import SecondaryButton from '../../../components/buttons/SecondaryButton';
import { useMediaQuery } from 'react-responsive';
import RESOURCE_CONFIGS from '../../../constants/resourceConfig';
import ConfirmModelComponent from '../../../components/model/ConfirmationModelComponent';
import classNames from 'classnames';
import './WeightCalculationTable.scss'
import { TareDataType } from './AverageTareTable';


interface WeightCalculationTableProp {
  t: TFunction;
  handleCancel?: () => void;
  handleBack?: () => void;
  handleNext?: () => void;
  disabled?: boolean;
  isViewMode?: boolean;
  weightData: WeightCalculationType[];
  setWeightData?: React.Dispatch<React.SetStateAction<WeightCalculationType[]>>;
  selectedUnit: string;
  setSelectedUnit?: React.Dispatch<React.SetStateAction<string>>;
  selectedTareOption: string;
  setSelectedTareOption?: React.Dispatch<React.SetStateAction<string>>;
  tareError?: string | null;
  weightError?: string | null;
  setWeightError?: React.Dispatch<React.SetStateAction<string | null>>;
  tareData?: TareDataType[];
}

interface WeightCalculationType {
  key: string;
  id: number;
  grossAmount: number;
  tare: number;
  netQuantity: number;
  declaredQuantity: number;
  individualError: number;
}

/* const initialWeightedCalculation = [
  {
    key: '1',
    id: 1,
    grossAmount: 0,
    tare: 0,
    netQuantity: 0,
    declaredQuantity: 0,
    individualError: 0,
  },
]; */

const WeightCalculationTable: React.FC<WeightCalculationTableProp> = ({
  t,
  handleCancel,
  handleNext,
  handleBack,
  disabled,
  isViewMode = false,
  weightData,
  setWeightData,
  selectedUnit,
  setSelectedUnit,
  selectedTareOption,
  setSelectedTareOption,
  tareError,
  weightError,
  setWeightError,
  tareData,
}) => {
  // const [weightData, setWeightData] = useState<WeightCalculationType[]>(initialWeightedCalculation);

  /* const [selectedUnit, setSelectedUnit] = useState<string>(prePackagedProductUnitEnum.GRAMS);
  const [selectedTareOption, setSelectedTareOption] = useState<string>('P');
 */
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
  const [enableNext, setEnableNext] = useState<boolean>(false);
  const isMobile = useMediaQuery({
    query: `(max-width: ${RESOURCE_CONFIGS.RESPONSIVE_BREAKPOINTS.SM})`,
  });

  const toggleCancelModal = () => {
    isCancelModalOpen ? setIsCancelModalOpen(false) : setIsCancelModalOpen(true);
  };

  useEffect(() => {
    // check weight table data
    const hasData = weightData?.every((row) => {
      return (
        (row.grossAmount || 0) !== 0 && (row.tare || 0) !== 0 && (row.declaredQuantity || 0) !== 0
      );
    });

    // check tare table data
    let isTareDataValid = false;
    if (tareData) {
      if (tareData.length === 1) {
        isTareDataValid = true;
      } else {
        isTareDataValid = tareData.every((t) => (t.tareValue || 0) > 0);
      }
    }

    // handle next button enable
    const nextEnabled = hasData && isTareDataValid;
    setEnableNext(nextEnabled);

  }, [weightData, tareData]);

  useEffect(() => {
    if (weightError) {
      const hasEmptyFields = weightData?.some(
        (row) => !row?.grossAmount || !row?.tare || !row?.declaredQuantity
      );
      if (hasEmptyFields) {
        if (weightError !== t('fillAllWeightValuesError')) {
          setWeightError?.(t('fillAllWeightValuesError'));
        }
      } else {
        setWeightError?.(null);
      }
    }
  }, [weightData, t, weightError, setWeightError]);

  const updateRow = (key: string, field: keyof WeightCalculationType, value: number | null) => {
    setWeightData?.((prev) =>
      prev.map((item) => {
        if (item?.key === key) {
          const newValue = value === null || value === undefined ? 0 : value;
          const updatedWeightData = { ...item, [field]: newValue };

          updatedWeightData.netQuantity = updatedWeightData?.grossAmount - updatedWeightData?.tare;
          updatedWeightData.individualError =
            updatedWeightData?.netQuantity - updatedWeightData?.declaredQuantity;
          return updatedWeightData;
        }
        return item;
      })
    );
  };

  const addRow = () => {
    const hasEmptyFields = weightData.some(
      (row) => !row?.grossAmount || !row?.tare || !row?.declaredQuantity
    );

    if (hasEmptyFields) {
      setWeightError?.(t('fillAllWeightValuesError'));
      return;
    }

    setWeightError?.(null);

    const newRow: WeightCalculationType = {
      key: Date.now().toString(),
      id: weightData?.length + 1,
      grossAmount: 0,
      tare: 0,
      netQuantity: 0,
      declaredQuantity: 0,
      individualError: 0,
    };
    setWeightData?.([...weightData, newRow]);
  };

  const deleteRow = (key: string) => {
    if (weightData?.length > 0) {
      const filtered = weightData?.filter((item) => item?.key !== key);
      const reIndexed = filtered?.map((item, index) => ({
        ...item,
        id: index + 1,
      }));
      setWeightData?.(reIndexed);
    }
  };

  const columns = [
    {
      title: t('no.'),
      dataIndex: 'id',
      key: 'id',
      width: 60,
      fixed: isMobile ? 'left' : undefined,
    },
    {
      title: (
        <div className="flex min-h-[60px] flex-col gap-2">
          <span>{t('grossAmount')}</span>
          <div className="w-full">
            <p>({t(`prePackagedProductUnitEnum.${selectedUnit}`)})<span className='column-asterisk'></span></p>
            {/* <Select
              placeholder={t('unitPlaceholder')}
              notFoundContent={t('noData')}
              options={getEnumOptionsWithTranslations(
                prePackagedProductUnitEnum,
                'prePackagedProductUnitEnum',
                t
              )}
              value={selectedUnit}
              onChange={(val) => setSelectedUnit?.(val)}
              disabled={isViewMode}
            /> */}
          </div>
        </div>
      ),
      dataIndex: 'grossAmount',
      key: 'grossAmount',
      render: (value: number, record: WeightCalculationType) => (
        <InputNumber
          type="number"
          className="view-mode-form w-full"
          value={value}
          disabled={isViewMode}
          step="0.01"
          min={0.0}
          status={value === null || value < 0 ? 'error' : ''}
          placeholder={t('grossAmountPlaceholder')}
          onChange={(val) => updateRow(record?.key, 'grossAmount', val)}
          onWheel={(e) => e.currentTarget.blur()}
          controls={true}
          precision={2}
          onKeyDown={(e) => {
            if (e.key === 'e' || e.key === 'E' || e.key === '-' || e.key === '+') {
              e.preventDefault();
            }
          }}
          formatter={(value) => {
            if (!value) return '';
            return String(value).replace(/^(\d*\.?\d{0,2}).*/, '$1');
          }}
          /* onKeyDown={(e) => {
            const isNumber = /[0-9]/.test(e.key);
            const isFirstDecimal = e.key === '.' && !value?.toString().includes('.');
            const isControlKey = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'];

            if (!isNumber && !isFirstDecimal && !isControlKey) {
              e.preventDefault();
            }
          }} */

          /* onInput={(value: string) => {
            if (value.includes('.')) {
              const [integer, fraction] = value.split('.');
              if (fraction && fraction.length > 2) {
                return `${integer}.${fraction.slice(0, 2)}`;
              }
            }
            return value;
          }}
          parser={(displayValue) => {
            if (!displayValue) return 0;
            
            const matched = displayValue.match(/^-?\d*(\.\d{0,2})?/);
            const cleanedValue = matched ? matched[0] : '0';

            return Number(cleanedValue);
          }} */
        />
      ),
    },
    {
      title: (
        <div className="flex min-h-[60px] flex-col gap-2">
          <span>{t('tare')}<span className='column-asterisk'></span></span>
          <Radio.Group
            size="small"
            value={selectedTareOption}
            onChange={(e) => setSelectedTareOption?.(e.target.value)}
            disabled={isViewMode}
            className="view-mode-form"
          >
            <Radio value={'P'}>
              <span>{t('P')}</span>
            </Radio>
            <Radio value={'I'}>
              <span>{t('I')}</span>
            </Radio>
          </Radio.Group>
        </div>
      ),
      dataIndex: 'tare',
      key: 'tare',
      render: (value: number, record: WeightCalculationType) => (
        <InputNumber
          type="number"
          className="view-mode-form w-full"
          value={value}
          disabled={isViewMode}
          step="0.01"
          min={0}
          status={value === null || value < 0 ? 'error' : ''}
          placeholder={t('tarePlaceholder')}
          onChange={(val) => updateRow(record?.key, 'tare', val)}
          /* onKeyDown={(e) => {
            const isNumber = /[0-9]/.test(e.key);
            const isFirstDecimal = e.key === '.' && !value?.toString().includes('.');
            const isControlKey = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'];

            if (!isNumber && !isFirstDecimal && !isControlKey) {
              e.preventDefault();
            }
          }} */
          onWheel={(e) => e.currentTarget.blur()}
          controls={true}
          precision={2}
          onKeyDown={(e) => {
            if (e.key === 'e' || e.key === 'E' || e.key === '-' || e.key === '+') {
              e.preventDefault();
            }
          }}
          formatter={(value) => {
            if (!value) return '';
            return String(value).replace(/^(\d*\.?\d{0,2}).*/, '$1');
          }}
        />
      ),
    },
    {
      title: (
        <div className="flex min-h-[60px] flex-col gap-2">
          <span>{t('netQuantity')}</span>
          <div className="w-full">
            <p>({t(`prePackagedProductUnitEnum.${selectedUnit}`)})</p>
            {/* <Select
              placeholder={t('unitPlaceholder')}
              notFoundContent={t('noData')}
              options={getEnumOptionsWithTranslations(
                prePackagedProductUnitEnum,
                'prePackagedProductUnitEnum',
                t
              )}
              value={selectedUnit}
              onChange={(val) => setSelectedUnit?.(val)}
              disabled={isViewMode}
            /> */}
          </div>
        </div>
      ),
      dataIndex: 'netQuantity',
      key: 'netQuantity',
      render: (_: any, record: WeightCalculationType) => {
        // const netQty = record?.grossAmount - record?.tare;
        // updateRow(record?.key, 'netQuantity', netQty)
        return <span>{record?.netQuantity?.toFixed(2)}</span>;
      },
    },
    {
      title: (
        <div className="flex min-h-[60px] flex-col gap-2">
          <span>{t('declaredQuantity')}</span>
          <div className="w-full">
            <p>({t(`prePackagedProductUnitEnum.${selectedUnit}`)})<span className='column-asterisk'></span></p>
            {/* <Select
              placeholder={t('unitPlaceholder')}
              notFoundContent={t('noData')}
              options={getEnumOptionsWithTranslations(
                prePackagedProductUnitEnum,
                'prePackagedProductUnitEnum',
                t
              )}
              value={selectedUnit}
              onChange={(val) => setSelectedUnit?.(val)}
              disabled={isViewMode}
            /> */}
          </div>
        </div>
      ),
      dataIndex: 'declaredQuantity',
      key: 'declaredQuantity',
      render: (value: number, record: WeightCalculationType) => (
        <InputNumber
          type="number"
          className="view-mode-form w-full"
          value={value}
          disabled={isViewMode}
          step="0.01"
          min={0.0}
          status={value === null || value < 0 ? 'error' : ''}
          placeholder={t('declaredQuantityPlaceholder')}
          onChange={(val) => updateRow(record?.key, 'declaredQuantity', val)}
          /* onKeyDown={(e) => {
            const isNumber = /[0-9]/.test(e.key);
            const isFirstDecimal = e.key === '.' && !value?.toString().includes('.');
            const isControlKey = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'];

            if (!isNumber && !isFirstDecimal && !isControlKey) {
              e.preventDefault();
            }
          }} */
          onWheel={(e) => e.currentTarget.blur()}
          controls={true}
          precision={2}
          onKeyDown={(e) => {
            if (e.key === 'e' || e.key === 'E' || e.key === '-' || e.key === '+') {
              e.preventDefault();
            }
          }}
          formatter={(value) => {
            if (!value) return '';
            return String(value).replace(/^(\d*\.?\d{0,2}).*/, '$1');
          }}
          defaultValue={0}
        />
      ),
    },
    {
      title: (
        <div className="flex min-h-[60px] flex-col gap-2">
          <span>{t('individualError')}</span>
          <div className="w-full">
            <p>({t(`prePackagedProductUnitEnum.${selectedUnit}`)})</p>
            {/* <Select
              placeholder={t('unitPlaceholder')}
              notFoundContent={t('noData')}
              options={getEnumOptionsWithTranslations(
                prePackagedProductUnitEnum,
                'prePackagedProductUnitEnum',
                t
              )}
              value={selectedUnit}
              onChange={(val) => setSelectedUnit?.(val)}
              disabled={isViewMode}
            /> */}
          </div>
        </div>
      ),
      dataIndex: 'individualError',
      key: 'individualError',
      render: (_: any, record: WeightCalculationType) => {
        // const individualErrorVal = record?.netQuantity - record?.declaredQuantity;
        return <span>{record?.individualError?.toFixed(2)}</span>;
      },
    },
    /* {
      title: <div className="text-center">{t('averageTareTableHeading')}</div>,
      children: [
        {
          title: t('no.'),
          dataIndex: 'id',
          key: 'id',
          width: '30%',
        },
        {
          title: t('tare'),
          dataIndex: 'tareValue',
          key: 'tareValue',
          render: (value: number, record: TareDataType) => (
            <InputNumber
              className="w-full"
              value={value}
              step="0.01"
              min={0}
              status={value === null || value < 0 ? 'error' : ''}
              placeholder={t('tarePlaceholder')}
              onChange={(val) => handleTareChange(record?.key, val)}
              onKeyDown={(e) => {
                const isNumber = /[0-9]/.test(e.key);
                const isFirstDecimal = e.key === '.' && !value?.toString().includes('.');
                const isControlKey = ['Backspace', 'Delete','Tab','ArrowLeft', 'ArrowRight']

                if (!isNumber && !isFirstDecimal && !isControlKey) {
                  e.preventDefault();
                }
              }}
            />
          ),
        },
      ],
    }, */
    {
      title: '',
      // title: t('action'),
      key: 'action',
      width: isMobile && !isViewMode ? 25 : 0, 
      fixed: isMobile && !isViewMode ? 'right' : undefined,
      onHeaderCell: () =>
        ({
          classNames: 'action-column-cell',
          id:'header-action-column'
        }) as React.HtmlHTMLAttributes<HTMLElement>,
      /* className: classNames({
        'border-none action-column-cell relative ': !isMobile,
        'bg-white aaa ': isMobile,
      }), */
      className: classNames({
        'border-none action-column-cell relative ': !isMobile,
        'bg-white border-white ': isMobile,
      }),
      render: (_: any, record: WeightCalculationType, index: number) => {
        if (index === 0 || isViewMode) return null;

        const deleteButton = (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteRow(record?.key)}
          />
        );

        if (isMobile) {
          return <div className="flex items-center justify-center pl-2">{deleteButton}</div>;
        }

        return <div className="absolute top-1/2 -translate-y-1/2">{deleteButton}</div>;
      },
    },
    /* {
      title: '',
      // title: t('action'),
      key: 'action',
      // width: 100,
      width: 0,
      onHeaderCell: () =>
        ({
          classNames: 'action-column-cell',
        }) as React.HtmlHTMLAttributes<HTMLElement>,
   
      classNames: 'border-none action-column-cell',
      render: (_: any, record: WeightCalculationType, index: number) => (
        <div className="absolute left-2 top-1/2 flex -translate-y-1/2 items-center">
          {index !== 0 && !isViewMode && (
            <Button
              type="text"
              danger
              disabled={index === 0}
              icon={<DeleteOutlined />}
              onClick={() => deleteRow(record?.key)}
              
            />
          )}
        </div>
      ),
    }, */
  ];

  return (
    <div className={`m-0 w-full ${isViewMode ? 'p-2' : ' py-4 pr-2 pl-4 md:p-8'}`}>
      <div className={`relative ${isViewMode ? 'ml-2' : 'ml-12 md:ml-8'}`}>
        {!isViewMode && (
          <div className="absolute -left-12 top-[98px] z-10">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={addRow}
              disabled={weightData?.some(
                (row) => !row.grossAmount || !row.tare || !row.declaredQuantity
              )}
            ></Button>
          </div>
        )}

        {/* </div> */}
        {/* <div>
        <Button type="primary" icon={<PlusOutlined />} onClick={addTare}></Button>
      </div> */}
        <div>
          <Form.Item
            name="selectedUnit"
            label={t('weightUnit')}
            rules={[
              {
                required: true,
                message: t('weightUnitRequired'),
              },
            ]}
            labelCol={{ xs: 24, md: 8 }}
            wrapperCol={{ xs: 22, md: 8 }}
            labelAlign="left"
            className="view-mode-form w-full md:w-1/3"
          >
            <Select
              placeholder={t('unitPlaceholder')}
              notFoundContent={t('noData')}
              options={getEnumOptionsWithTranslations(
                prePackagedProductUnitEnum,
                'prePackagedProductUnitEnum',
                t
              )}
              value={selectedUnit}
              onChange={(val) => setSelectedUnit?.(val)}
              disabled={isViewMode}
            />
            <span className="hidden">{selectedUnit}</span>
          </Form.Item>
        </div>
        <div className="w-full pr-0 md:pr-4">
          <Table
            dataSource={weightData}
            columns={columns}
            bordered
            pagination={false}
            //   summary={calculateAverageTare}
            // rowClassName="h-[54px]"
            className="weight-Table"
            scroll={isMobile ? { x: 800 } : undefined}
            // sticky={true}
          />
          {weightError && (
            <div className="mt-2 text-md text-red-500">
              {weightError}
            </div>
          )}
        </div>
      </div>
      {!isViewMode && (
        <div className="mt-8 flex w-full flex-col justify-end gap-4 md:ml-auto md:w-1/2 md:flex-row">
          <DefaultButton handleOnClick={toggleCancelModal} width="100%">
            {t('cancel')}
          </DefaultButton>
          <SecondaryButton handleOnClick={() => handleBack?.()} width="100%">
            {t('back')}
          </SecondaryButton>
          {enableNext ? (
            <PrimaryButton
              handleOnClick={(e) => {
                e.preventDefault();
                handleNext?.();
              }}
              width="100%"
              disabled={disabled || isViewMode}
              hideBgGradient={true}
            >
              {t('next')}
            </PrimaryButton>
          ) : (
            <SecondaryButton
              handleOnClick={(e) => {
                e.preventDefault();
                handleNext?.();
                // handleNext?.();
              }}
              width="100%"
              disabled={!enableNext || isViewMode}
            >
              {t('next')}
            </SecondaryButton>
          )}
        </div>
      )}

      <ConfirmModelComponent
        isOpen={isCancelModalOpen}
        onClickedCancel={toggleCancelModal}
        header={t('verificationCancelModalTitle')}
        content={<p>{t('verificationCancelModalConfirmaiton')}</p>}
        footerAction={{
          confimation: {
            label: t('yes'),
            onClick: handleCancel ?? (() => {}),
          },
          cancel: {
            label: t('no'),
            onClick: toggleCancelModal,
          },
        }}
        styleClasses="z-100"
      />
    </div>
  );
};

export default WeightCalculationTable;
